import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, Plus, Pencil, Trash2, Copy, Shield, Receipt } from 'lucide-react'
import { api } from '@payroll/utils/api'
import { formatCurrency, formatDate, formatNumber } from '@payroll/utils/format'
import type { SocialSecurityRate, TaxBracket, TaxAllowance } from '@payroll/types'
import { Button } from '@payroll/components/ui/button'
import { Input } from '@payroll/components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@payroll/components/ui/card'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@payroll/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@payroll/components/ui/dialog'
import { Select } from '@payroll/components/ui/select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@payroll/components/ui/tabs'

// ---------------------------------------------------------------------------
// Social Security Rate Form
// ---------------------------------------------------------------------------

interface SSRateFormData {
  rate_type: 'employee' | 'employer'
  rate_percent: string
  max_salary_base: string
  effective_from: string
  effective_to: string
  description: string
}

const INITIAL_SS_FORM: SSRateFormData = {
  rate_type: 'employee',
  rate_percent: '5',
  max_salary_base: '17500',
  effective_from: '',
  effective_to: '',
  description: '',
}

function AddSSRateDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<SSRateFormData>(INITIAL_SS_FORM)

  const maxContribution = useMemo(() => {
    const rate = parseFloat(form.rate_percent) || 0
    const base = parseFloat(form.max_salary_base) || 0
    return (rate / 100) * base
  }, [form.rate_percent, form.max_salary_base])

  const mutation = useMutation({
    mutationFn: (data: SSRateFormData) =>
      api.post('/settings/social-security-rates', {
        rate_type: data.rate_type,
        rate_percent: parseFloat(data.rate_percent),
        max_salary_base: parseFloat(data.max_salary_base),
        effective_from: data.effective_from,
        effective_to: data.effective_to || null,
        description: data.description || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-security-rates'] })
      setForm(INITIAL_SS_FORM)
      onOpenChange(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(form)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Social Security Rate</DialogTitle>
          <DialogDescription>
            Create a new social security rate entry. Previous rates will remain in history.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate Type</label>
            <Select
              value={form.rate_type}
              onChange={(e) => setForm({ ...form, rate_type: e.target.value as 'employee' | 'employer' })}
            >
              <option value="employee">Employee</option>
              <option value="employer">Employer</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate %</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.rate_percent}
              onChange={(e) => setForm({ ...form, rate_percent: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Salary Base (Baht)</label>
            <Input
              type="number"
              step="1"
              min="0"
              value={form.max_salary_base}
              onChange={(e) => setForm({ ...form, max_salary_base: e.target.value })}
              required
            />
          </div>

          <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
            <p className="text-sm text-blue-800">
              Max Contribution: <span className="font-semibold">{formatCurrency(maxContribution)}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
              <Input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective To (optional)</label>
              <Input
                type="date"
                value={form.effective_to}
                onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Standard rate effective Jan 2026"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save Rate'}
            </Button>
          </DialogFooter>

          {mutation.isError && (
            <p className="text-sm text-red-600">
              Error: {(mutation.error as Error).message}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Social Security Tab
// ---------------------------------------------------------------------------

function SocialSecurityTab() {
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const { data: ratesResponse, isLoading } = useQuery({
    queryKey: ['social-security-rates'],
    queryFn: () => api.get<SocialSecurityRate[]>('/settings/social-security-rates'),
  })

  const rates = ratesResponse?.data ?? []

  // Find current active rates (no effective_to or effective_to >= today)
  const today = new Date().toISOString().split('T')[0]
  const activeEmployeeRate = rates.find(
    (r) =>
      r.rate_type === 'employee' &&
      r.effective_from <= today &&
      (!r.effective_to || r.effective_to >= today)
  )
  const activeEmployerRate = rates.find(
    (r) =>
      r.rate_type === 'employer' &&
      r.effective_from <= today &&
      (!r.effective_to || r.effective_to >= today)
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Active Rates Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#1a237e]" />
              Employee Rate
            </CardTitle>
            <CardDescription>Current active contribution rate</CardDescription>
          </CardHeader>
          <CardContent>
            {activeEmployeeRate ? (
              <div className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[#1a237e]">
                    {activeEmployeeRate.rate_percent}%
                  </span>
                  <Badge>Active</Badge>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Max Salary Base: {formatCurrency(activeEmployeeRate.max_salary_base)}</p>
                  <p>
                    Max Contribution:{' '}
                    {formatCurrency(
                      activeEmployeeRate.max_contribution ??
                        (activeEmployeeRate.rate_percent / 100) * activeEmployeeRate.max_salary_base
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No active rate configured</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#1a237e]" />
              Employer Rate
            </CardTitle>
            <CardDescription>Current active contribution rate</CardDescription>
          </CardHeader>
          <CardContent>
            {activeEmployerRate ? (
              <div className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[#1a237e]">
                    {activeEmployerRate.rate_percent}%
                  </span>
                  <Badge>Active</Badge>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Max Salary Base: {formatCurrency(activeEmployerRate.max_salary_base)}</p>
                  <p>
                    Max Contribution:{' '}
                    {formatCurrency(
                      activeEmployerRate.max_contribution ??
                        (activeEmployerRate.rate_percent / 100) * activeEmployerRate.max_salary_base
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No active rate configured</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rate History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Rate History</CardTitle>
              <CardDescription>All social security rate records</CardDescription>
            </div>
            <Button size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Rate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Effective From</TableHead>
                <TableHead>Effective To</TableHead>
                <TableHead className="text-right">Rate %</TableHead>
                <TableHead className="text-right">Max Salary Base</TableHead>
                <TableHead className="text-right">Max Contribution</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                    No rates configured yet
                  </TableCell>
                </TableRow>
              ) : (
                rates.map((rate) => {
                  const maxContrib =
                    rate.max_contribution ?? (rate.rate_percent / 100) * rate.max_salary_base
                  const isActive =
                    rate.effective_from <= today && (!rate.effective_to || rate.effective_to >= today)
                  return (
                    <TableRow key={rate.id}>
                      <TableCell>
                        <Badge variant={rate.rate_type === 'employee' ? 'default' : 'secondary'}>
                          {rate.rate_type === 'employee' ? 'Employee' : 'Employer'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(rate.effective_from)}</TableCell>
                      <TableCell>{rate.effective_to ? formatDate(rate.effective_to) : '-'}</TableCell>
                      <TableCell className="text-right font-medium">{rate.rate_percent}%</TableCell>
                      <TableCell className="text-right">{formatCurrency(rate.max_salary_base)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(maxContrib)}</TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {rate.description || '-'}
                        {isActive && (
                          <Badge variant="outline" className="ml-2 text-green-700 border-green-300">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddSSRateDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tax Bracket Dialog (Add / Edit)
// ---------------------------------------------------------------------------

interface TaxBracketFormData {
  bracket_order: string
  min_income: string
  max_income: string
  rate_percent: string
}

function TaxBracketDialog({
  open,
  onOpenChange,
  year,
  editingBracket,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  year: number
  editingBracket: TaxBracket | null
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<TaxBracketFormData>({
    bracket_order: editingBracket?.bracket_order.toString() ?? '1',
    min_income: editingBracket?.min_income.toString() ?? '0',
    max_income: editingBracket?.max_income?.toString() ?? '',
    rate_percent: editingBracket?.rate_percent.toString() ?? '0',
  })

  // Reset form when editingBracket changes
  React.useEffect(() => {
    if (open) {
      setForm({
        bracket_order: editingBracket?.bracket_order.toString() ?? '1',
        min_income: editingBracket?.min_income.toString() ?? '0',
        max_income: editingBracket?.max_income?.toString() ?? '',
        rate_percent: editingBracket?.rate_percent.toString() ?? '0',
      })
    }
  }, [open, editingBracket])

  const isEdit = editingBracket !== null

  const mutation = useMutation({
    mutationFn: (data: TaxBracketFormData) => {
      const payload = {
        bracket_order: parseInt(data.bracket_order),
        min_income: parseFloat(data.min_income),
        max_income: data.max_income ? parseFloat(data.max_income) : null,
        rate_percent: parseFloat(data.rate_percent),
        year,
      }
      if (isEdit) {
        return api.put(`/settings/tax-brackets/${editingBracket.id}`, payload)
      }
      return api.post('/settings/tax-brackets', payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-brackets', year] })
      onOpenChange(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(form)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Tax Bracket' : 'Add Tax Bracket'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the tax bracket details.' : `Add a new tax bracket for year ${year}.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bracket Order</label>
            <Input
              type="number"
              min="1"
              value={form.bracket_order}
              onChange={(e) => setForm({ ...form, bracket_order: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Income (Baht)</label>
              <Input
                type="number"
                min="0"
                value={form.min_income}
                onChange={(e) => setForm({ ...form, min_income: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Income (Baht, blank = no limit)</label>
              <Input
                type="number"
                min="0"
                value={form.max_income}
                onChange={(e) => setForm({ ...form, max_income: e.target.value })}
                placeholder="No limit"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate %</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.rate_percent}
              onChange={(e) => setForm({ ...form, rate_percent: e.target.value })}
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Update Bracket' : 'Add Bracket'}
            </Button>
          </DialogFooter>

          {mutation.isError && (
            <p className="text-sm text-red-600">
              Error: {(mutation.error as Error).message}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Tax Allowance Dialog (Add / Edit)
// ---------------------------------------------------------------------------

interface TaxAllowanceFormData {
  allowance_type: string
  amount: string
  description: string
}

function TaxAllowanceDialog({
  open,
  onOpenChange,
  year,
  editingAllowance,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  year: number
  editingAllowance: TaxAllowance | null
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<TaxAllowanceFormData>({
    allowance_type: editingAllowance?.allowance_type ?? '',
    amount: editingAllowance?.amount.toString() ?? '',
    description: editingAllowance?.description ?? '',
  })

  React.useEffect(() => {
    if (open) {
      setForm({
        allowance_type: editingAllowance?.allowance_type ?? '',
        amount: editingAllowance?.amount.toString() ?? '',
        description: editingAllowance?.description ?? '',
      })
    }
  }, [open, editingAllowance])

  const isEdit = editingAllowance !== null

  const mutation = useMutation({
    mutationFn: (data: TaxAllowanceFormData) => {
      const payload = {
        allowance_type: data.allowance_type,
        amount: parseFloat(data.amount),
        description: data.description || null,
        year,
      }
      if (isEdit) {
        return api.put(`/settings/tax-allowances/${editingAllowance.id}`, payload)
      }
      return api.post('/settings/tax-allowances', payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-allowances', year] })
      onOpenChange(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(form)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Tax Allowance' : 'Add Tax Allowance'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the allowance details.' : `Add a new tax allowance for year ${year}.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allowance Type</label>
            <Input
              value={form.allowance_type}
              onChange={(e) => setForm({ ...form, allowance_type: e.target.value })}
              placeholder="e.g. personal, spouse, child, insurance"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Baht)</label>
            <Input
              type="number"
              min="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Personal Allowance"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Update Allowance' : 'Add Allowance'}
            </Button>
          </DialogFooter>

          {mutation.isError && (
            <p className="text-sm text-red-600">
              Error: {(mutation.error as Error).message}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Tax Bracket Visual Bar Chart
// ---------------------------------------------------------------------------

const BRACKET_COLORS = [
  'bg-green-200',
  'bg-green-400',
  'bg-yellow-300',
  'bg-yellow-500',
  'bg-orange-400',
  'bg-orange-600',
  'bg-red-400',
  'bg-red-600',
  'bg-red-800',
]

function TaxBracketChart({ brackets }: { brackets: TaxBracket[] }) {
  if (brackets.length === 0) return null

  const sorted = [...brackets].sort((a, b) => a.bracket_order - b.bracket_order)
  const maxRate = Math.max(...sorted.map((b) => b.rate_percent), 1)

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">Tax Rate Visualization</h4>
      <div className="flex items-end gap-1 h-32">
        {sorted.map((bracket, i) => {
          const heightPercent = maxRate > 0 ? (bracket.rate_percent / maxRate) * 100 : 0
          const color = BRACKET_COLORS[i % BRACKET_COLORS.length]
          return (
            <div
              key={bracket.id}
              className="flex flex-1 flex-col items-center justify-end gap-1"
            >
              <span className="text-xs font-semibold text-gray-700">{bracket.rate_percent}%</span>
              <div
                className={`w-full rounded-t ${color} transition-all`}
                style={{ height: `${Math.max(heightPercent, 4)}%` }}
                title={`${formatNumber(bracket.min_income)} - ${bracket.max_income ? formatNumber(bracket.max_income) : 'unlimited'}`}
              />
              <span className="text-[10px] text-gray-500 text-center leading-tight">
                {bracket.min_income >= 1_000_000
                  ? `${(bracket.min_income / 1_000_000).toFixed(1)}M`
                  : bracket.min_income >= 1_000
                  ? `${(bracket.min_income / 1_000).toFixed(0)}K`
                  : formatNumber(bracket.min_income)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tax Configuration Tab
// ---------------------------------------------------------------------------

function TaxConfigTab() {
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)

  // Bracket dialogs
  const [bracketDialogOpen, setBracketDialogOpen] = useState(false)
  const [editingBracket, setEditingBracket] = useState<TaxBracket | null>(null)

  // Allowance dialogs
  const [allowanceDialogOpen, setAllowanceDialogOpen] = useState(false)
  const [editingAllowance, setEditingAllowance] = useState<TaxAllowance | null>(null)

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  // Queries
  const { data: bracketsResponse, isLoading: bracketsLoading } = useQuery({
    queryKey: ['tax-brackets', selectedYear],
    queryFn: () => api.get<TaxBracket[]>(`/settings/tax-brackets?year=${selectedYear}`),
  })

  const { data: allowancesResponse, isLoading: allowancesLoading } = useQuery({
    queryKey: ['tax-allowances', selectedYear],
    queryFn: () => api.get<TaxAllowance[]>(`/settings/tax-allowances?year=${selectedYear}`),
  })

  const brackets = bracketsResponse?.data ?? []
  const allowances = allowancesResponse?.data ?? []
  const sortedBrackets = [...brackets].sort((a, b) => a.bracket_order - b.bracket_order)

  // Copy year mutation
  const copyYearMutation = useMutation({
    mutationFn: () =>
      api.post('/settings/tax-brackets/copy-year', {
        from_year: selectedYear,
        to_year: selectedYear + 1,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-brackets', selectedYear + 1] })
      setSelectedYear(selectedYear + 1)
    },
  })

  // Delete bracket mutation
  const deleteBracketMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/settings/tax-brackets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-brackets', selectedYear] })
    },
  })

  const handleEditBracket = (bracket: TaxBracket) => {
    setEditingBracket(bracket)
    setBracketDialogOpen(true)
  }

  const handleAddBracket = () => {
    setEditingBracket(null)
    setBracketDialogOpen(true)
  }

  const handleDeleteBracket = (bracket: TaxBracket) => {
    if (window.confirm(`Delete bracket #${bracket.bracket_order} (${bracket.rate_percent}%)?`)) {
      deleteBracketMutation.mutate(bracket.id)
    }
  }

  const handleEditAllowance = (allowance: TaxAllowance) => {
    setEditingAllowance(allowance)
    setAllowanceDialogOpen(true)
  }

  const handleAddAllowance = () => {
    setEditingAllowance(null)
    setAllowanceDialogOpen(true)
  }

  const isLoading = bracketsLoading || allowancesLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Tax Year:</label>
        <Select
          className="w-32"
          value={selectedYear.toString()}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </div>

      {/* Tax Brackets */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Tax Brackets ({selectedYear})</CardTitle>
              <CardDescription>Progressive income tax rate schedule</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyYearMutation.mutate()}
                disabled={copyYearMutation.isPending}
              >
                <Copy className="mr-2 h-4 w-4" />
                {copyYearMutation.isPending ? 'Copying...' : `Copy to ${selectedYear + 1}`}
              </Button>
              <Button size="sm" onClick={handleAddBracket}>
                <Plus className="mr-2 h-4 w-4" />
                Add Bracket
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {copyYearMutation.isError && (
            <p className="text-sm text-red-600">
              Copy failed: {(copyYearMutation.error as Error).message}
            </p>
          )}
          {copyYearMutation.isSuccess && (
            <p className="text-sm text-green-600">
              Successfully copied brackets to {selectedYear} (switched to new year).
            </p>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Order</TableHead>
                <TableHead>Income Range</TableHead>
                <TableHead className="text-right">Rate %</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBrackets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                    No tax brackets configured for {selectedYear}
                  </TableCell>
                </TableRow>
              ) : (
                sortedBrackets.map((bracket) => (
                  <TableRow key={bracket.id}>
                    <TableCell className="font-medium">{bracket.bracket_order}</TableCell>
                    <TableCell>
                      {formatCurrency(bracket.min_income)}
                      {' - '}
                      {bracket.max_income ? formatCurrency(bracket.max_income) : 'No limit'}
                    </TableCell>
                    <TableCell className="text-right font-medium">{bracket.rate_percent}%</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditBracket(bracket)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteBracket(bracket)}
                          disabled={deleteBracketMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Visual Chart */}
          <TaxBracketChart brackets={sortedBrackets} />
        </CardContent>
      </Card>

      {/* Tax Allowances */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Tax Allowances ({selectedYear})</CardTitle>
              <CardDescription>Personal deductions and allowances</CardDescription>
            </div>
            <Button size="sm" onClick={handleAddAllowance}>
              <Plus className="mr-2 h-4 w-4" />
              Add Allowance
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-16 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allowances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                    No allowances configured for {selectedYear}
                  </TableCell>
                </TableRow>
              ) : (
                allowances.map((allowance) => (
                  <TableRow key={allowance.id}>
                    <TableCell className="font-medium">{allowance.allowance_type}</TableCell>
                    <TableCell className="text-right">{formatCurrency(allowance.amount)}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {allowance.description || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditAllowance(allowance)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <TaxBracketDialog
        open={bracketDialogOpen}
        onOpenChange={setBracketDialogOpen}
        year={selectedYear}
        editingBracket={editingBracket}
      />
      <TaxAllowanceDialog
        open={allowanceDialogOpen}
        onOpenChange={setAllowanceDialogOpen}
        year={selectedYear}
        editingAllowance={editingAllowance}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Settings Page (Main Export)
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage social security rates, tax brackets, and allowances
        </p>
      </div>

      <Tabs defaultValue="social-security">
        <TabsList>
          <TabsTrigger value="social-security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Social Security Rates
          </TabsTrigger>
          <TabsTrigger value="tax-config" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Tax Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="social-security">
          <SocialSecurityTab />
        </TabsContent>

        <TabsContent value="tax-config">
          <TaxConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
